import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createStream } from '../../../store/actions/StreamActions';
import { selectMaxStreams } from '../../../store/selectors/SubscriptionSelectors';
import { selectIsAdmin } from '../../../store/selectors/AuthSelectors';
import swal from 'sweetalert';
import { PageHeader, Card, Button, Field, Input, inputClass } from '../../ui';

const CreateStream = ({ creating, error, createStream, streams, maxStreams, isAdmin }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', description: '', serverUrl: '' });
  const [touched, setTouched] = useState(false);

  const streamCount = streams ? streams.length : 0;
  const isAtLimit = !isAdmin && maxStreams > 0 && streamCount >= maxStreams;
  const usagePercent = maxStreams > 0 ? Math.min(Math.round((streamCount / maxStreams) * 100), 100) : 0;
  const usageTone = usagePercent >= 100 ? 'live' : usagePercent >= 80 ? 'warn' : 'good';
  const toneBg = { live: 'tw-bg-live', warn: 'tw-bg-warn', good: 'tw-bg-good' }[usageTone];

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const showLimitDialog = (limit, current) => {
    swal({
      title: 'Stream Limit Reached',
      text: `Your plan allows ${limit || maxStreams} stream${(limit || maxStreams) !== 1 ? 's' : ''}. Upgrade to create more.`,
      icon: 'warning',
      buttons: { cancel: 'Cancel', upgrade: { text: 'Upgrade Plan', value: 'upgrade' } },
    }).then((v) => v === 'upgrade' && navigate('/pricing'));
  };

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!form.name.trim()) return;
    if (isAtLimit) return showLimitDialog(maxStreams, streamCount);

    createStream(
      { name: form.name, description: form.description, serverUrl: form.serverUrl || window.location.hostname },
      (err, result) => {
        if (!err && result) {
          swal({ title: 'Stream Created!', text: 'Your stream is ready.', icon: 'success', button: 'View Details' })
            .then(() => navigate(`/streams/${result.id}`));
        } else if (err?.code === 'STREAM_LIMIT_REACHED' || err?.status === 403 || err?.message?.toLowerCase().includes('limit')) {
          showLimitDialog(err?.limit, err?.current);
        } else {
          swal('Error!', err?.message || 'Failed to create stream', 'error');
        }
      }
    );
  };

  return (
    <>
      <PageHeader title="Create Stream" subtitle="Set up a new RTMP ingest point for broadcasting" />

      {isAtLimit && (
        <div className="tw-mb-6 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-warn/30 tw-bg-warn/10 tw-px-4 tw-py-3 tw-text-sm tw-text-warn">
          <i className="fa-solid fa-triangle-exclamation" />
          <span className="tw-mr-auto"><strong>Stream limit reached.</strong> Delete a stream or upgrade your plan to create a new one.</span>
          <Button variant="outline" size="sm" icon="fa-arrow-up" onClick={() => navigate('/pricing')}>Upgrade</Button>
        </div>
      )}

      <div className="tw-grid tw-gap-6 lg:tw-grid-cols-3">
        <div className="lg:tw-col-span-2">
          <Card>
            {error && (
              <div className="tw-mb-4 tw-rounded-lg tw-border tw-border-live/30 tw-bg-live/10 tw-px-3 tw-py-2 tw-text-sm tw-text-live">{error}</div>
            )}
            <form onSubmit={submit} className="tw-space-y-5">
              <Field label="Stream name" required error={touched && !form.name.trim() ? 'Stream name is required.' : null}>
                <Input name="name" value={form.name} onChange={set('name')} placeholder="e.g. Sunday Service — Main Camera" disabled={creating} />
              </Field>
              <Field label="Description" hint="Optional — shown in your stream list.">
                <textarea className={inputClass} rows={3} value={form.description} onChange={set('description')} placeholder="What is this stream for?" disabled={creating} />
              </Field>
              <Field label="Stream server URL" hint="Leave empty to use the default server (hostname only, no port).">
                <div className="tw-relative">
                  <Input name="serverUrl" value={form.serverUrl} onChange={set('serverUrl')} placeholder={window.location.hostname} disabled={creating} className="tw-pr-10" />
                  <i className="fa-solid fa-server tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-content-faint" />
                </div>
              </Field>
              <div className="tw-flex tw-justify-between tw-pt-2">
                <Button variant="secondary" type="button" onClick={() => navigate('/streams')} disabled={creating}>Cancel</Button>
                <Button variant="primary" type="submit" icon={creating ? 'fa-spinner fa-spin' : 'fa-plus'} disabled={creating || isAtLimit}>
                  {creating ? 'Creating…' : 'Create Stream'}
                </Button>
              </div>
            </form>
          </Card>
        </div>

        <div className="tw-space-y-6">
          {!isAdmin && maxStreams > 0 && (
            <Card title="Stream Usage">
              <div className="tw-mb-2 tw-flex tw-justify-between tw-text-sm">
                <span className="tw-text-content-muted">Streams used</span>
                <span className="tw-font-bold tw-text-heading">{streamCount} / {maxStreams}</span>
              </div>
              <div className="tw-h-2 tw-overflow-hidden tw-rounded-full tw-bg-ink-750">
                <div className={`tw-h-full tw-rounded-full ${toneBg}`} style={{ width: `${usagePercent}%` }} />
              </div>
              <p className="tw-mt-2 tw-text-xs tw-text-content-faint">
                {isAtLimit ? 'Limit reached — delete a stream or upgrade.' : `${maxStreams - streamCount} remaining on your plan`}
              </p>
              {isAtLimit && (
                <Button variant="outline" size="sm" icon="fa-arrow-up" className="tw-mt-3 tw-w-full" onClick={() => navigate('/pricing')}>Upgrade Plan</Button>
              )}
            </Card>
          )}

          <Card title="What you'll get">
            <ul className="tw-space-y-2 tw-text-sm">
              {[
                ['RTMP URL', 'for OBS / streaming software'],
                ['Stream key', 'unique, regeneratable identifier'],
                ['HLS + DASH URLs', 'for players and embeds'],
                ['Real-time status', 'viewers, bitrate, health'],
              ].map(([t, d]) => (
                <li key={t} className="tw-flex tw-gap-2">
                  <i className="fa-solid fa-check tw-mt-0.5 tw-text-good" />
                  <span><strong className="tw-text-heading">{t}</strong> <span className="tw-text-content-muted">{d}</span></span>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="OBS Studio settings">
            <pre className="tw-overflow-x-auto tw-rounded-lg tw-bg-ink-950 tw-p-3 tw-text-xs tw-text-content-muted">
{`Service:    Custom
Server:     rtmp://your-server/live
Stream Key: (auto-generated)`}
            </pre>
          </Card>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  creating: state.streams.creating,
  error: state.streams.error,
  streams: state.streams.streams,
  maxStreams: selectMaxStreams(state),
  isAdmin: selectIsAdmin(state),
});

export default connect(mapStateToProps, { createStream })(CreateStream);
